import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import EventImageDropzone from '../../components/admin/EventImageDropzone';
import BookedCalls from '../../components/admin/BookedCalls';
import { useAuth } from '../../context/AuthContext';
import * as adminService from '../../services/admin.service';
import api from '../../services/api';

// Google Calendar's real, fixed 11-color event palette (verified against the
// live Hub Events calendar 2026-08-28) — a plain per-event picker, not
// automatic host detection; staff already pick these by hand today.
const CALENDAR_COLOR_OPTIONS = [
  { id: '1',  name: 'Lavender', hex: '#a4bdfc' },
  { id: '2',  name: 'Sage', hex: '#7ae7bf' },
  { id: '3',  name: 'Grape', hex: '#dbadff' },
  { id: '4',  name: 'Flamingo', hex: '#ff887c' },
  { id: '5',  name: 'Banana', hex: '#fbd75b' },
  { id: '6',  name: 'Tangerine', hex: '#ffb878' },
  { id: '7',  name: 'Peacock', hex: '#46d6db' },
  { id: '8',  name: 'Graphite', hex: '#e1e1e1' },
  { id: '9',  name: 'Blueberry', hex: '#5484ed' },
  { id: '10', name: 'Basil', hex: '#51b749' },
  { id: '11', name: 'Tomato', hex: '#dc2127' },
];

function ColorSelect({ value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">Calendar color (optional)</label>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onChange('')}
          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] text-gray-400 ${!value ? 'border-reset-terracotta' : 'border-gray-200'}`}
          title="No color (calendar default)">
          ✕
        </button>
        {CALENDAR_COLOR_OPTIONS.map(c => (
          <button key={c.id} type="button" onClick={() => onChange(c.id)} title={c.name}
            className={`w-7 h-7 rounded-full border-2 ${value === c.id ? 'border-reset-terracotta' : 'border-transparent'}`}
            style={{ backgroundColor: c.hex }} />
        ))}
      </div>
    </div>
  );
}

const REPLAY_BADGE = {
  none:       'bg-gray-100 text-gray-500',
  processing: 'bg-amber-50 text-amber-600',
  ready:      'bg-reset-sage/10 text-reset-sage',
  failed:     'bg-red-50 text-red-600',
};

function eventDate(ev) {
  return ev.event_datetime ? new Date(ev.event_datetime) : (ev.scheduled_date ? new Date(ev.scheduled_date) : null);
}

// Team365 staff span two zones — Jenn in Atlantic, Trish/Patti in Ontario
// (Eastern) — so times are always shown in both explicitly, computed via a
// fixed IANA zone rather than the viewer's own browser/OS timezone. Relying
// on ambient local time was the actual bug (2026-08-28): the same stored,
// correct instant read differently depending on whose machine was looking
// at it. TIMEZONE_IANA/TIMEZONE_LABEL mirror server/src/utils/zonedTime.js.
const TIMEZONE_IANA = { AT: 'America/Halifax', ET: 'America/New_York' };
function formatTimeInZone(d, zoneKey) {
  return new Intl.DateTimeFormat('en-US', { timeZone: TIMEZONE_IANA[zoneKey], hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
}
function formatEventTime(ev) {
  const d = eventDate(ev);
  if (!d) return null;
  return `${formatTimeInZone(d, 'AT')} AT · ${formatTimeInZone(d, 'ET')} ET`;
}

// Jenn, 2026-09-05: today's Writer's Room vanished from this panel 2
// minutes after it started, while she was trying to host it — this checked
// event_datetime (the START), so anything already underway looked "past"
// the instant it began. Members join late, so an event needs to keep
// showing until close to its real end, not its start. Matches the same
// end-minus-buffer pattern getUpcoming already uses for members, now with
// her explicit 15-minute buffer instead of 5.
function eventEndDate(ev) {
  if (ev.event_end_datetime) return new Date(ev.event_end_datetime);
  const start = eventDate(ev);
  if (!start) return null;
  return new Date(start.getTime() + 60 * 60000);
}

function isPastEvent(ev) {
  const end = eventEndDate(ev);
  if (!end) return false;
  return end.getTime() - 15 * 60000 < Date.now();
}

function hoursSince(dateStr) {
  if (!dateStr) return null;
  return (Date.now() - new Date(dateStr).getTime()) / 3600000;
}

// Source of a row, purely from how its id was minted — Zoom and Google
// Calendar rows are both real, editable events (via their own API); only
// course-content rows have no real meeting behind them to act on.
function eventSource(ev) {
  if (ev.zoom_meeting_id) return 'zoom';
  if (ev.ghl_event_id?.startsWith('gcal_')) return 'gcal';
  if (ev.ghl_event_id?.startsWith('course_')) return 'course';
  return 'manual';
}
const SOURCE_LABEL = { zoom: 'Zoom', gcal: 'Google Calendar', course: 'Course', manual: 'Manual' };

// `datetime-local` inputs want "YYYY-MM-DDTHH:mm" — showing it as wall-clock
// time in a specific zone (not the browser's own ambient one, which is the
// bug this whole timezone rework fixes) so editing shows what that occurrence
// actually reads as in whichever zone the editor currently has selected.
function toDatetimeLocalValueInZone(iso, zoneKey) {
  if (!iso) return '';
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE_IANA[zoneKey], hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const parts = fmt.formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
}

// For pure calendar-grid cells (abstract Y/M/D, no real instant behind them,
// generated via local Date arithmetic) — safe to read back with ambient
// local getters since they were built with the local constructor too.
function toDayKey(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// For a REAL event's absolute timestamp — which calendar day it lands in
// has to be read in AT specifically, not the viewer's ambient browser/OS
// zone. Found 2026-08-28: an event correctly stored for Sept 1 at 8pm AT
// was landing in the Sept 2 grid cell because the grouping logic read the
// day-of-month using the browser's own local getters instead of AT's.
function toDayKeyInZone(d, zoneKey = 'AT') {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE_IANA[zoneKey] });
  return fmt.format(d); // en-CA gives YYYY-MM-DD directly
}

const DAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABEL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Standard 6-week (42-day) month grid, Sunday-start, for a given month's 1st. */
function buildMonthDays(monthStart) {
  const firstOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function buildWeekDays(anyDayInWeek) {
  const start = new Date(anyDayInWeek);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

export default function AdminHubEvents({ embedded = false, linkedEventId = null }) {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [error, setError]   = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState('month'); // day | week | month | 3month
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null); // 'zoom' | 'calendar' | null
  const [loadingCalLink, setLoadingCalLink] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // null | 'create' | 'edit' | 'image'
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const rows = await adminService.getHubEventsCache();
      setEvents(rows);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load Hub events.');
    }
  }, []);

  // Google Calendar edits made outside Team365 only reach us via the
  // 15-minute background sync — Refresh forces that sync right now instead
  // of just re-reading whatever's already in our cache, so "I just changed
  // it in Google Calendar" shows up immediately instead of "in a few minutes."
  const handleRefresh = async () => {
    setSyncing(true);
    setError(null);
    try {
      await adminService.runHubSync();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not sync with Google Calendar.');
    } finally {
      setSyncing(false);
    }
  };

  const loadGcalStatus = useCallback(async () => {
    try {
      const { connected } = await adminService.getGoogleCalendarStatus();
      setGcalConnected(connected);
    } catch {
      setGcalConnected(null);
    }
  }, []);

  useEffect(() => { load(); loadGcalStatus(); }, [load, loadGcalStatus]);

  // Deep-linked straight to one specific event (e.g. from Video Library's
  // "still in use" message) — select it directly by id rather than making
  // Jenn find it by title/date, since same-named canceled duplicates make
  // that genuinely error-prone.
  useEffect(() => {
    if (!linkedEventId || !events) return;
    if (events.some(e => e.id === linkedEventId)) {
      setSelectedEventId(linkedEventId);
      setShowPast(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedEventId, events]);

  const gcalJustConnected = searchParams.get('gcal_connected');
  const gcalError = searchParams.get('gcal_error');
  useEffect(() => {
    if (!gcalJustConnected && !gcalError) return;
    if (gcalJustConnected) setGcalConnected(true);
    const next = new URLSearchParams(searchParams);
    next.delete('gcal_connected');
    next.delete('gcal_error');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gcalJustConnected, gcalError]);

  const { upcoming, past } = useMemo(() => {
    if (!events) return { upcoming: [], past: [] };
    const up = [], pa = [];
    for (const ev of events) {
      (ev.canceled_at || isPastEvent(ev) ? pa : up).push(ev);
    }
    up.sort((a, b) => new Date(eventDate(a)) - new Date(eventDate(b)));
    pa.sort((a, b) => new Date(eventDate(b)) - new Date(eventDate(a)));
    return { upcoming: up, past: pa };
  }, [events]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of upcoming) {
      const d = eventDate(ev);
      if (!d) continue;
      const key = toDayKeyInZone(d, 'AT');
      (map[key] ||= []).push(ev);
    }
    return map;
  }, [upcoming]);

  // Today's events, right at the top — the real usability gap (Jenn, live
  // event 2026-09-20): a host had to scroll past the empty tools area to
  // reach the calendar grid, pick a day, pick the event, then scroll back
  // UP to reach Start Hosting (which renders above the calendar, not next
  // to where they clicked). Today's events one-click-away at the top,
  // combined with the auto-scroll below, means a host never has to hunt.
  const todaysEvents = eventsByDay[toDayKeyInZone(new Date(), 'AT')] || [];

  const shiftAnchor = (dir) => {
    const d = new Date(anchorDate);
    if (viewMode === 'day') d.setDate(d.getDate() + dir);
    else if (viewMode === 'week') d.setDate(d.getDate() + dir * 7);
    else if (viewMode === 'month') d.setMonth(d.getMonth() + dir);
    else d.setMonth(d.getMonth() + dir * 3);
    setAnchorDate(d);
  };

  const updateEventInList = (updated) => setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));

  const selectedEvent = useMemo(() => events?.find(e => e.id === selectedEventId) || null, [events, selectedEventId]);
  // Zoom and Google-Calendar-sourced events are both real, editable events —
  // Change/Delete/Start Hosting write back to whichever is the real source.
  // Only Learning Lab course-content rows (no real meeting behind them at
  // all) aren't actionable here.
  const canChangeSelected = selectedEvent && ['zoom', 'gcal'].includes(eventSource(selectedEvent));

  const selectEvent = (ev) => {
    setSelectedEventId(id => (id === ev.id ? null : ev.id));
    setActivePanel(null);
  };

  // Real fix, not the first attempt: an inline "Selected event" card still
  // meant a host had to scroll to find it, wherever it happened to land on
  // the page. Jenn, live on staging, 2026-09-23: "no dialogue box opens...
  // it takes a lot to navigate with all the scrolling." What she wants is a
  // real popup — click any event, everything about it is right there, no
  // scrolling at all. Picking an event now opens a real dialog (see the
  // EventDialog wrapper below) instead of revealing more page content to
  // hunt for.
  useEffect(() => {
    if (!selectedEventId) return;
    const onKey = (e) => { if (e.key === 'Escape') { setSelectedEventId(null); setActivePanel(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEventId]);

  // Copy-to-clipboard for pushing attendance externally (Facebook, etc.) —
  // Jenn, 2026-08-31: "We use Facebook for that, which means I need access
  // to links." The Zoom link is already on the event; the Calendar link
  // isn't stored (Google's htmlLink encoding isn't safely reconstructible
  // from just the raw event id) so it's fetched live on click.
  const copyToClipboard = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(which);
      setTimeout(() => setCopiedLink(c => (c === which ? null : c)), 1500);
    } catch {
      window.prompt('Copy this link:', text);
    }
  };

  const copyZoomLink = (ev) => {
    if (!ev.event_url) { setError('No Zoom/join link on this event yet.'); return; }
    copyToClipboard(ev.event_url, 'zoom');
  };

  const copyCalendarLink = async (ev) => {
    if (!ev.gcal_event_id) { setError('This event hasn\'t been pushed to the Live Hub Events calendar yet.'); return; }
    setLoadingCalLink(true);
    setError(null);
    try {
      const { url } = await adminService.getHubEventCalendarLink(ev.id);
      if (!url) { setError('Could not find this event on the calendar.'); return; }
      copyToClipboard(url, 'calendar');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not fetch the calendar link.');
    } finally {
      setLoadingCalLink(false);
    }
  };

  const handleStartHosting = async (ev) => {
    setBusyId(ev.id);
    setError(null);
    // 'noopener' makes window.open() return null by spec — that silently broke
    // this: `tab` was always null, so the blank tab we opened here never got
    // navigated anywhere, and the fallback window.open(startUrl) after the
    // await got blocked as a non-user-initiated popup. Net effect: a blank
    // tab that stays blank forever. Dropping noopener keeps a real reference
    // to navigate once startUrl comes back.
    const tab = window.open('', '_blank');
    try {
      const { startUrl } = await adminService.startZoomHosting(ev.id);
      if (tab) tab.location.href = startUrl;
      else window.open(startUrl, '_blank');
    } catch (err) {
      if (tab) tab.close();
      setError(err.response?.data?.error || 'Could not start Zoom hosting for this event.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (ev, deleteScope) => {
    if (deleteScope !== 'all' && !window.confirm(`Cancel "${ev.title}"? This removes it from Zoom or Google Calendar (whichever it's really on) and takes it off members' event lists.`)) return;
    setBusyId(ev.id);
    setError(null);
    try {
      await adminService.deleteHubEvent(ev.id, deleteScope);
      setSelectedEventId(null);
      setActivePanel(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete this event.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRefreshReplay = async (ev) => {
    setBusyId(ev.id);
    setError(null);
    try {
      const { event } = await adminService.refreshEventReplay(ev.id);
      updateEventInList(event);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not refresh replay status.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDetachReplay = async (ev) => {
    if (!window.confirm(`Take the replay off the members' Replays page for "${ev.title}"? The video stays in your Video Library.`)) return;
    setBusyId(ev.id);
    setError(null);
    try {
      const { event } = await adminService.detachEventReplay(ev.id);
      updateEventInList(event);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not detach replay.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveOneVideo = async (ev, bunnyVideoId, label) => {
    if (!window.confirm(`Delete ${label} from "${ev.title}"? This deletes the video from Bunny too — any other recording attached to this event stays.`)) return;
    setBusyId(ev.id);
    setError(null);
    try {
      const { event } = await adminService.removeOneEventReplayVideo(ev.id, bunnyVideoId);
      updateEventInList(event);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that video.');
    } finally {
      setBusyId(null);
    }
  };

  const selectDay = (dayKey) => setSelectedDayKey(prev => (prev === dayKey ? null : dayKey));

  const content = (
    <>
      {/* One click to the thing a host actually came here for — today's
          events, right at the top, no calendar-hunting required. */}
      {todaysEvents.length > 0 && (
        <div className="bg-reset-creamDark rounded-2xl p-4 mb-4 border border-reset-terracotta/20">
          <p className="text-xs font-semibold text-reset-terracotta uppercase tracking-wide mb-2">Today</p>
          <div className="flex flex-wrap gap-2">
            {todaysEvents.map(ev => (
              <button
                key={ev.id}
                onClick={() => selectEvent(ev)}
                className="px-3 py-2 rounded-xl text-sm font-medium text-left bg-white text-reset-brown shadow-sm hover:shadow transition-shadow"
              >
                {ev.title}
                <span className="block text-xs font-normal text-gray-400">{formatEventTime(ev)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          {!embedded && <h1 className="font-serif text-2xl font-bold text-reset-brown">Hub Events</h1>}
          <p className="text-sm text-gray-500 mt-0.5">
            {events ? `${upcoming.length} upcoming · ${past.length} past` : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {gcalConnected === false && (
            <button onClick={adminService.connectGoogleCalendar}
              className="px-4 py-2 bg-white border border-reset-terracotta text-reset-terracotta text-sm font-medium rounded-xl hover:bg-reset-terracotta hover:text-white transition-colors">
              Connect Google Calendar
            </button>
          )}
          {gcalConnected === true && (
            <span className="text-xs text-reset-sage px-2 py-1 rounded-full bg-reset-sage/10 font-medium">✓ Google Calendar connected</span>
          )}
          <button onClick={handleRefresh} disabled={syncing}
            className="px-4 py-2 bg-white border border-gray-200 text-sm font-medium rounded-xl hover:border-reset-terracotta transition-colors disabled:opacity-50">
            {syncing ? 'Syncing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {gcalJustConnected && (
        <div className="mb-4 bg-reset-sage/10 border border-reset-sage/30 text-reset-sage rounded-xl p-4 text-sm">
          Google Calendar connected — Zoom events will now push to the Live Hub Events calendar automatically.
        </div>
      )}
      {gcalError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          Google Calendar connection {gcalError === 'denied' ? 'was canceled' : 'failed'} — try "Connect Google Calendar" again.
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
      )}

      {/* Jenn, 2026-09-23: "you have to scroll down to get it and select it
          and then scroll up and then look through multiple buttons for the
          one you want." Change/Delete/Image used to live in a row up here,
          separate from the event they act on and only enabled once
          something was picked below — real distance between an action and
          its target. They now live inside the event's own dialog (below),
          right next to Start Hosting. The only thing that belongs at the
          top, with nothing selected, is starting something new. */}
      <button
        onClick={() => { setActivePanel(p => (p === 'create' ? null : 'create')); setSelectedEventId(null); }}
        className={`w-full py-4 text-base font-semibold rounded-2xl transition-colors shadow-sm mb-4
          ${activePanel === 'create' ? 'bg-reset-brown text-white' : 'bg-reset-terracotta text-white hover:bg-reset-brown'}`}
      >
        + Create Event
      </button>

      {selectedEvent && (
        <EventDialog onClose={() => { setSelectedEventId(null); setActivePanel(null); }}>
          <div className="flex items-center gap-2 flex-wrap pr-6">
            <span className="text-lg font-semibold text-reset-brown">{selectedEvent.title}</span>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{SOURCE_LABEL[eventSource(selectedEvent)]}</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5 mb-4">{formatEventTime(selectedEvent)}</p>

          {!canChangeSelected && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-4">
              This is a Learning Lab content item, not a real event — nothing to change, delete, or host here.
            </p>
          )}

          {/* Everything you'd do with this one event, in one place — the
              real ask (Jenn, 2026-09-23): "click the event, have it open
              and have all of our options in there." Start Hosting is the
              thing a host needs most, so it's the one that's big. */}
          <button
            onClick={() => handleStartHosting(selectedEvent)}
            disabled={busyId === selectedEvent.id || !canChangeSelected}
            className="w-full py-4 bg-reset-terracotta text-white text-base font-semibold rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-40 mb-3"
          >
            {busyId === selectedEvent.id ? 'Starting…' : '▶ Start Hosting'}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => copyZoomLink(selectedEvent)}
              title="Copy the Zoom/join link — for pushing attendance on Facebook, etc."
              className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
            >
              {copiedLink === 'zoom' ? 'Copied!' : '🔗 Copy Zoom link'}
            </button>
            <button
              onClick={() => copyCalendarLink(selectedEvent)}
              disabled={loadingCalLink}
              title="Copy the Live Hub Events Google Calendar link for this event"
              className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:border-reset-sage hover:text-reset-sage transition-colors disabled:opacity-50"
            >
              {loadingCalLink ? 'Fetching…' : copiedLink === 'calendar' ? 'Copied!' : '📅 Copy Calendar link'}
            </button>
            <button
              onClick={() => setActivePanel(p => (p === 'edit' ? null : 'edit'))}
              disabled={!canChangeSelected}
              className={`px-3 py-2 text-xs font-medium rounded-xl border transition-colors disabled:opacity-40
                ${activePanel === 'edit' ? 'border-reset-terracotta bg-reset-terracotta/5 text-reset-brown' : 'border-gray-200 text-reset-brown hover:border-reset-terracotta'}`}
            >
              ✎ Change
            </button>
            <button
              onClick={() => setActivePanel(p => (p === 'image' ? null : 'image'))}
              className={`px-3 py-2 text-xs font-medium rounded-xl border transition-colors
                ${activePanel === 'image' ? 'border-reset-terracotta bg-reset-terracotta/5 text-reset-brown' : 'border-gray-200 text-reset-brown hover:border-reset-terracotta'}`}
            >
              🖼 Image
            </button>
            <button
              onClick={() => {
                if (selectedEvent.zoom_occurrence_id || selectedEvent.gcal_recurring_event_id) {
                  setActivePanel(p => (p === 'delete' ? null : 'delete'));
                } else {
                  handleDelete(selectedEvent);
                }
              }}
              disabled={!canChangeSelected || busyId === selectedEvent.id}
              className={`px-3 py-2 text-xs font-medium rounded-xl border transition-colors disabled:opacity-40
                ${activePanel === 'delete' ? 'border-red-300 bg-red-50 text-red-600' : 'border-gray-200 text-red-500 hover:border-red-300'}`}
            >
              ✕ Delete
            </button>
          </div>

          {selectedEvent.zoom_meeting_id && <WhoCame eventId={selectedEvent.id} />}

          {selectedEvent.replay_status && selectedEvent.replay_status !== 'none' && (
            <div className="mt-3 pt-3 border-t border-gray-50">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${REPLAY_BADGE[selectedEvent.replay_status] || REPLAY_BADGE.none}`}>
                  replay: {selectedEvent.replay_status}
                </span>
                {selectedEvent.additional_replay_bunny_video_ids?.length > 0 && (
                  <span className="text-[10px] text-gray-400">
                    {1 + selectedEvent.additional_replay_bunny_video_ids.length} recordings attached — keep the one you want, delete the rest individually
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {selectedEvent.replay_bunny_video_id && (
                  <div className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-gray-500 font-mono truncate">Primary — {selectedEvent.replay_bunny_video_id}</span>
                    <button
                      onClick={() => handleRemoveOneVideo(selectedEvent, selectedEvent.replay_bunny_video_id, 'the primary recording')}
                      disabled={busyId === selectedEvent.id}
                      className="text-xs font-medium text-red-600 hover:underline disabled:opacity-40 shrink-0"
                    >
                      Delete
                    </button>
                  </div>
                )}
                {(selectedEvent.additional_replay_bunny_video_ids || []).map((videoId, i) => (
                  <div key={videoId} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-gray-500 font-mono truncate">Part {i + 2} — {videoId}</span>
                    <button
                      onClick={() => handleRemoveOneVideo(selectedEvent, videoId, `Part ${i + 2}`)}
                      disabled={busyId === selectedEvent.id}
                      className="text-xs font-medium text-red-600 hover:underline disabled:opacity-40 shrink-0"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activePanel === 'edit' && canChangeSelected && (
            <div className="mt-3 pt-3 border-t border-gray-50">
              <EditEventForm
                event={selectedEvent}
                onSaved={(updated) => { updateEventInList(updated); setActivePanel(null); }}
                onSavedRecurring={async () => { setActivePanel(null); setSelectedEventId(null); await load(); }}
                onSavedSeries={async () => { setActivePanel(null); setSelectedEventId(null); await load(); }}
                onError={setError}
              />
            </div>
          )}
          {activePanel === 'image' && (
            <div className="mt-3 pt-3 border-t border-gray-50">
              <EventImageDropzone
                eventId={selectedEvent.id}
                eventTitle={selectedEvent.title}
                onSaved={() => setActivePanel(null)}
                onError={setError}
                onCancel={() => setActivePanel(null)}
              />
            </div>
          )}
          {activePanel === 'delete' && (
            <div className="mt-3 pt-3 border-t border-gray-50">
              <p className="text-sm text-reset-brown mb-3">
                "{selectedEvent.title}" repeats. Delete just this one time, or the whole series?
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleDelete(selectedEvent, 'one')}
                  disabled={busyId === selectedEvent.id}
                  className="px-4 py-2 bg-white border-2 border-gray-200 text-reset-brown text-sm font-semibold rounded-xl hover:border-red-300 disabled:opacity-40"
                >
                  Delete this one only
                </button>
                <button
                  onClick={() => handleDelete(selectedEvent, 'all')}
                  disabled={busyId === selectedEvent.id}
                  className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-xl hover:bg-red-600 disabled:opacity-40"
                >
                  Delete all upcoming events in this series
                </button>
                <button
                  onClick={() => setActivePanel(null)}
                  disabled={busyId === selectedEvent.id}
                  className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-reset-brown"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </EventDialog>
      )}

      {activePanel === 'create' && (
        <CreateEventForm
          onCreated={(ev) => { setEvents(prev => [ev, ...(prev || [])]); setActivePanel(null); }}
          onCreatedRecurring={async () => { setActivePanel(null); await load(); }}
          onError={setError}
        />
      )}

      {!events ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          {viewMode === 'day' ? (
            <div className="mb-6">
              <CalendarNav viewMode={viewMode} setViewMode={setViewMode} anchorDate={anchorDate} setAnchorDate={setAnchorDate} shiftAnchor={shiftAnchor} />
              <DayEventsPanel
                dayKey={toDayKey(anchorDate)}
                dayEvents={eventsByDay[toDayKey(anchorDate)] || []}
                selectedEventId={selectedEventId}
                onSelectEvent={selectEvent}
              />
            </div>
          ) : (
            <div className="mb-6">
              <CalendarNav viewMode={viewMode} setViewMode={setViewMode} anchorDate={anchorDate} setAnchorDate={setAnchorDate} shiftAnchor={shiftAnchor} />
              <CalendarGrid
                viewMode={viewMode}
                anchorDate={anchorDate}
                eventsByDay={eventsByDay}
                selectedDayKey={selectedDayKey}
                onSelectDay={selectDay}
                selectedEventId={selectedEventId}
                onSelectEvent={selectEvent}
              />
              {selectedDayKey && (
                <div className="mt-4">
                  <DayEventsPanel
                    dayKey={selectedDayKey}
                    dayEvents={eventsByDay[selectedDayKey] || []}
                    selectedEventId={selectedEventId}
                    onSelectEvent={selectEvent}
                  />
                </div>
              )}
            </div>
          )}

          <PastEventsAccordion
            open={showPast}
            onToggle={() => setShowPast(o => !o)}
            pastEvents={past}
            busyId={busyId}
            onRefreshReplay={handleRefreshReplay}
            onDetachReplay={handleDetachReplay}
            onAttached={updateEventInList}
            onError={setError}
          />
        </>
      )}

      {/* Booked Calls (Jenn, Oct 7 2026) — Jenn-level only, not in BackEnd's embedded copy. */}
      {!embedded && user?.can_manage_affiliates && <BookedCalls />}
    </>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />
        {content}
      </div>
    </DashboardLayout>
  );
}

function CalendarNav({ viewMode, setViewMode, anchorDate, setAnchorDate, shiftAnchor }) {
  const label = useMemo(() => {
    if (viewMode === 'day') {
      return anchorDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    }
    if (viewMode === 'week') {
      const days = buildWeekDays(anchorDate);
      const a = days[0], b = days[6];
      return a.getMonth() === b.getMonth()
        ? `${MONTH_LABEL[a.getMonth()]} ${a.getDate()}–${b.getDate()}, ${a.getFullYear()}`
        : `${MONTH_LABEL[a.getMonth()]} ${a.getDate()} – ${MONTH_LABEL[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;
    }
    if (viewMode === 'month') return `${MONTH_LABEL[anchorDate.getMonth()]} ${anchorDate.getFullYear()}`;
    const end = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 2, 1);
    return `${MONTH_LABEL[anchorDate.getMonth()]} – ${MONTH_LABEL[end.getMonth()]} ${end.getFullYear()}`;
  }, [viewMode, anchorDate]);

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
      <div className="flex items-center gap-2">
        <button onClick={() => shiftAnchor(-1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:border-reset-terracotta text-gray-500">‹</button>
        <span className="text-sm font-semibold text-reset-brown min-w-[180px]">{label}</span>
        <button onClick={() => shiftAnchor(1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:border-reset-terracotta text-gray-500">›</button>
        <button onClick={() => setAnchorDate(new Date())} className="ml-1 text-xs font-medium text-reset-terracotta hover:underline">Today</button>
      </div>
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {['day', 'week', 'month', '3month'].map(v => (
          <button key={v} onClick={() => setViewMode(v)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${viewMode === v ? 'bg-white text-reset-terracotta shadow-sm' : 'text-gray-500 hover:text-reset-brown'}`}>
            {v === '3month' ? '3 Months' : v[0].toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

// Every event, full time + title, no truncation — timing is the whole point
// of a planning view, so "+N more" hides exactly the info that matters.
// Each one is its own clickable target — selects that exact event directly,
// no separate "click the day, then click the event" step.
const COLOR_ID_TO_HEX = Object.fromEntries(CALENDAR_COLOR_OPTIONS.map(c => [c.id, c.hex]));

/**
 * A real popup for one event — everything about it, right where you clicked,
 * regardless of scroll position. Replaces an earlier attempt (an inline card
 * further down the page) that Jenn caught live on staging: "no dialogue box
 * opens... it takes a lot to navigate with all the scrolling." Backdrop
 * click and the corner × both close it; Escape is handled by the caller
 * (it also needs to clear activePanel, which this component doesn't know about).
 */
// ─── Who came (Zoom attendance) ─────────────────────────────────────────────
// Jenn, 2026-09-28: "If we're tracking, we should actually track. Plus, I want
// to know if any non members are sneaking in." Everyone Zoom reports joining
// this event, however they got there (Google Calendar, email, text, My Hub).
// "Not recognised" = couldn't match to a member by email or full name — shown
// in calm amber, never alarm red (Jenn: loud alerts hurt her head); each one
// also creates a To-Do.
function WhoCame({ eventId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setData(null); setError(null);
    api.get(`/admin/hub-events/${eventId}/attendance`)
      .then(r => setData(r.data))
      .catch(() => setError('Could not load who came.'));
  }, [eventId]);
  return (
    <div className="mt-3 pt-3 border-t border-gray-50">
      <p className="text-xs font-semibold text-reset-brown mb-1">Who came</p>
      {error && <p className="text-xs text-gray-500">{error}</p>}
      {!data && !error && <p className="text-xs text-gray-400">Loading…</p>}
      {data && data.people.length === 0 && (
        <p className="text-xs text-gray-500">Nobody recorded yet. Attendance is counted from Zoom once someone joins.</p>
      )}
      {data && data.people.length > 0 && (
        <>
          <p className="text-xs text-gray-500 mb-2">
            {data.counts.members} {data.counts.members === 1 ? 'member' : 'members'} · {data.counts.staff} Team365
            {data.counts.unrecognised > 0 && <> · <span className="font-semibold text-amber-800">{data.counts.unrecognised} not recognised</span></>}
          </p>
          <ul className="space-y-1">
            {data.people.map((p, i) => (
              <li key={i} className={`text-xs flex items-center justify-between gap-2 rounded-md px-2 py-1 ${p.kind === 'unrecognised' ? 'bg-amber-50 text-amber-900' : 'text-gray-600'}`}>
                <span>
                  {p.name}
                  {p.kind === 'member' && p.zoomName && p.zoomName !== p.name && <span className="text-gray-400"> (in Zoom as "{p.zoomName}")</span>}
                </span>
                <span className="shrink-0 text-[10px] font-medium">{p.kind === 'member' ? 'Member' : p.kind === 'staff' ? 'Team365' : 'Not recognised'}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function EventDialog({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 bg-reset-brown/40 flex items-start sm:items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-lg relative my-8 sm:my-0">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-300 hover:text-reset-brown text-2xl leading-none"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}

function DayCellChips({ dayEvents, selectedEventId, onSelectEvent }) {
  if (!dayEvents?.length) return null;
  return (
    <div className="space-y-0.5 mt-1">
      {dayEvents.map(ev => {
        const hex = ev.gcal_color_id && COLOR_ID_TO_HEX[ev.gcal_color_id];
        return (
        <button
          key={ev.id}
          onClick={(e) => { e.stopPropagation(); onSelectEvent(ev); }}
          style={hex ? { backgroundColor: hex } : undefined}
          className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded transition-colors
            ${selectedEventId === ev.id ? 'ring-2 ring-reset-terracotta' : ''}
            ${hex ? 'text-reset-brown hover:brightness-95' : (selectedEventId === ev.id ? 'bg-reset-terracotta text-white' : 'bg-reset-terracotta/10 text-reset-brown hover:bg-reset-terracotta/20')}`}
        >
          <span className="font-semibold">{formatEventTime(ev)}</span> {ev.title}
        </button>
        );
      })}
    </div>
  );
}

function MonthGrid({ monthStart, eventsByDay, selectedDayKey, onSelectDay, selectedEventId, onSelectEvent }) {
  const days = buildMonthDays(monthStart);
  const today = toDayKey(new Date());
  return (
    <div className="bg-white rounded-2xl shadow-sm p-3">
      <div className="grid grid-cols-7 gap-1">
        {DAY_LABEL.map(d => <div key={d} className="text-[10px] font-medium text-gray-400 text-center pb-1">{d[0]}</div>)}
        {days.map((d, i) => {
          const key = toDayKey(d);
          const inMonth = d.getMonth() === monthStart.getMonth();
          const dayEvents = eventsByDay[key] || [];
          return (
            <div
              key={i}
              onClick={() => dayEvents.length > 0 && onSelectDay(key)}
              className={`text-left rounded-lg border p-1 min-h-[64px] align-top transition-colors
                ${!inMonth ? 'opacity-30' : ''}
                ${key === selectedDayKey ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-100'}
                ${dayEvents.length > 0 ? 'hover:border-reset-terracotta cursor-pointer' : ''}
                ${key === today ? 'ring-1 ring-reset-sage' : ''}`}
            >
              <span className="text-[11px] text-gray-500">{d.getDate()}</span>
              <DayCellChips dayEvents={dayEvents} selectedEventId={selectedEventId} onSelectEvent={onSelectEvent} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarGrid({ viewMode, anchorDate, eventsByDay, selectedDayKey, onSelectDay, selectedEventId, onSelectEvent }) {
  if (viewMode === 'week') {
    const days = buildWeekDays(anchorDate);
    const today = toDayKey(new Date());
    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map(d => {
          const key = toDayKey(d);
          const dayEvents = eventsByDay[key] || [];
          return (
            <div key={key} onClick={() => dayEvents.length > 0 && onSelectDay(key)}
              className={`bg-white rounded-xl shadow-sm p-2 text-left min-h-[100px] border transition-colors
                ${key === selectedDayKey ? 'border-reset-terracotta' : 'border-transparent'}
                ${dayEvents.length > 0 ? 'hover:border-reset-terracotta cursor-pointer' : ''}
                ${key === today ? 'ring-1 ring-reset-sage' : ''}`}>
              <p className="text-[10px] font-medium text-gray-400">{DAY_LABEL[d.getDay()]}</p>
              <p className="text-sm font-semibold text-reset-brown mb-1">{d.getDate()}</p>
              <DayCellChips dayEvents={dayEvents} selectedEventId={selectedEventId} onSelectEvent={onSelectEvent} />
            </div>
          );
        })}
      </div>
    );
  }

  if (viewMode === 'month') {
    return <MonthGrid monthStart={anchorDate} eventsByDay={eventsByDay} selectedDayKey={selectedDayKey} onSelectDay={onSelectDay} selectedEventId={selectedEventId} onSelectEvent={onSelectEvent} />;
  }

  // 3month — three full months, stacked vertically (scroll), each with the
  // same full detail as the single-month view. Side-by-side was unreadable
  // at this density; stacking keeps every day's real event list visible.
  const months = [0, 1, 2].map(offset => new Date(anchorDate.getFullYear(), anchorDate.getMonth() + offset, 1));
  return (
    <div className="space-y-4">
      {months.map((m, i) => (
        <div key={i}>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">{MONTH_LABEL[m.getMonth()]} {m.getFullYear()}</p>
          <MonthGrid monthStart={m} eventsByDay={eventsByDay} selectedDayKey={selectedDayKey} onSelectDay={onSelectDay} selectedEventId={selectedEventId} onSelectEvent={onSelectEvent} />
        </div>
      ))}
    </div>
  );
}

function DayEventsPanel({ dayKey, dayEvents, selectedEventId, onSelectEvent }) {
  const label = useMemo(() => {
    const [y, m, d] = dayKey.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }, [dayKey]);

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">{label}</p>
      {dayEvents.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
          No events this day.
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
          {dayEvents.map(ev => {
            const source = eventSource(ev);
            const stuckHours = ev.replay_status === 'processing' ? hoursSince(ev.replay_added_at) : null;
            const isStuck = stuckHours != null && stuckHours > 24;
            return (
              <button
                key={ev.id}
                onClick={() => onSelectEvent(ev)}
                className={`w-full text-left flex items-center justify-between gap-3 px-5 py-3 flex-wrap transition-colors
                  ${selectedEventId === ev.id ? 'bg-reset-terracotta/5' : 'hover:bg-gray-50'}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-reset-brown">{ev.title}</span>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{SOURCE_LABEL[source]}</span>
                    {ev.replay_status && ev.replay_status !== 'none' && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isStuck ? 'bg-red-50 text-red-600' : (REPLAY_BADGE[ev.replay_status] || REPLAY_BADGE.none)}`}>
                        {isStuck ? `replay stuck ${Math.floor(stuckHours / 24)}d` : `replay: ${ev.replay_status}`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{formatEventTime(ev)}</p>
                </div>
                {selectedEventId === ev.id && <span className="text-xs font-medium text-reset-terracotta shrink-0">Selected</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PastEventsAccordion({ open, onToggle, pastEvents, busyId, onRefreshReplay, onDetachReplay, onAttached, onError }) {
  const [attachFormFor, setAttachFormFor] = useState(null);
  const [renameFor, setRenameFor] = useState(null);
  return (
    <div className="mt-2">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl shadow-sm text-sm font-medium text-gray-500 hover:text-reset-brown transition-colors">
        <span>{open ? '▾' : '▸'} Past &amp; canceled events</span>
        <span className="text-gray-300">{pastEvents.length}</span>
      </button>
      {open && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50 mt-2">
          {pastEvents.length === 0 ? (
            <p className="text-sm text-gray-400 px-5 py-6 text-center">Nothing here yet.</p>
          ) : pastEvents.map(ev => (
            <div key={ev.id} className={`px-5 py-3 ${ev.canceled_at ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-reset-brown truncate">{ev.title}</span>
                    {ev.canceled_at && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">Canceled</span>}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{formatEventTime(ev)} · {new Date(eventDate(ev)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  {ev.replay_status === 'ready' && ev.replay_title?.trim() && (
                    <p className="text-xs text-gray-500 mt-0.5">On Replays as: <span className="font-medium">{ev.replay_title}</span></p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {ev.replay_status === 'ready' ? (
                    <>
                      <button onClick={() => setRenameFor(id => (id === ev.id ? null : ev.id))}
                        className="px-3 py-1.5 border border-gray-200 text-xs font-medium rounded-lg text-gray-500 hover:border-reset-terracotta transition-colors">
                        {renameFor === ev.id ? 'Cancel' : 'Rename'}
                      </button>
                      <button onClick={() => onDetachReplay(ev)} disabled={busyId === ev.id}
                        className="px-3 py-1.5 border border-gray-200 text-xs font-medium rounded-lg text-gray-500 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50">
                        Remove from Replays
                      </button>
                    </>
                  ) : ev.replay_status === 'processing' ? (
                    <button onClick={() => onRefreshReplay(ev)} disabled={busyId === ev.id}
                      className="px-3 py-1.5 border border-gray-200 text-xs font-medium rounded-lg text-gray-500 hover:border-reset-terracotta transition-colors disabled:opacity-50">
                      {busyId === ev.id ? 'Checking…' : 'Check status'}
                    </button>
                  ) : (
                    <button onClick={() => setAttachFormFor(id => (id === ev.id ? null : ev.id))}
                      className="px-3 py-1.5 border border-gray-200 text-xs font-medium rounded-lg text-gray-500 hover:border-reset-terracotta transition-colors">
                      {attachFormFor === ev.id ? 'Cancel' : (ev.replay_status === 'failed' ? 'Retry attach' : 'Attach replay')}
                    </button>
                  )}
                </div>
              </div>
              {attachFormFor === ev.id && (
                <AttachReplayForm event={ev} onAttached={(updated) => { onAttached(updated); setAttachFormFor(null); }} onError={onError} />
              )}
              {renameFor === ev.id && ev.replay_status === 'ready' && (
                <RenameReplayForm event={ev} onSaved={(updated) => { onAttached(updated); setRenameFor(null); }} onError={onError} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachReplayForm({ event, onAttached, onError }) {
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceAuthHeader, setSourceAuthHeader] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { event: updated } = await adminService.attachEventReplay(event.id, {
        sourceUrl: sourceUrl.trim(),
        sourceAuthHeader: sourceAuthHeader.trim() || undefined,
      });
      onAttached(updated);
    } catch (err) {
      onError(err.response?.data?.error || 'Could not attach replay.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-50 space-y-4">
    <PickFromLibrary event={event} onAttached={onAttached} onError={onError} />
    <form onSubmit={submit} className="space-y-2">
      <p className="text-xs font-semibold text-gray-500">Or copy a video in from a web address</p>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Replay source URL</label>
        <input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} required
          placeholder="https://…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Source auth header (optional)</label>
        <input value={sourceAuthHeader} onChange={e => setSourceAuthHeader(e.target.value)}
          placeholder="e.g. Bearer …"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      <button type="submit" disabled={saving}
        className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50">
        {saving ? 'Attaching…' : 'Attach'}
      </button>
    </form>
    </div>
  );
}

// Jenn, 2026-10-09: a video she uploaded straight to Bunny (Patti's
// meditation) never reached Replays. Pick it from the Video Library instead —
// no web address, no second copy.
function PickFromLibrary({ event, onAttached, onError }) {
  const [search, setSearch] = useState('');
  const [videos, setVideos] = useState(null);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api.get('/admin/bunny-library', { params: { search: search.trim() || undefined, page: 1 } })
        .then(r => { if (live) setVideos(r.data.items || []); })
        .catch(() => { if (live) { setVideos([]); onError('Could not load the Video Library.'); } });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [search, onError]);

  const choose = async (v) => {
    setSavingId(v.id);
    try {
      const { event: updated } = await adminService.attachExistingEventReplay(event.id, v.id);
      onAttached(updated);
    } catch (err) {
      onError(err.response?.data?.error || 'Could not attach that video.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-500">Pick a video already in your Video Library</p>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, e.g. Mindful Minutes"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      {videos === null ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : videos.length === 0 ? (
        <p className="text-xs text-gray-400">No videos found.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-lg">
          {videos.map(v => (
            <div key={v.id} className="flex items-center gap-3 px-3 py-2">
              {v.thumbnailUrl
                ? <img src={v.thumbnailUrl} alt="" className="w-16 h-10 object-cover rounded shrink-0 bg-gray-100" />
                : <div className="w-16 h-10 rounded bg-gray-100 shrink-0" />}
              <span className="text-sm text-reset-brown flex-1 min-w-0 truncate">{v.title}</span>
              {v.durationSeconds > 0 && <span className="text-xs text-gray-400 shrink-0">{Math.round(v.durationSeconds / 60)} min</span>}
              <button onClick={() => choose(v)} disabled={!!savingId}
                className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50 shrink-0">
                {savingId === v.id ? 'Adding…' : 'Use this'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The name members see on the Replays page; blank goes back to the event's own title.
function RenameReplayForm({ event, onSaved, onError }) {
  const [title, setTitle] = useState(event.replay_title || event.title || '');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { event: updated } = await adminService.setEventReplayTitle(event.id, title.trim() === event.title ? '' : title);
      onSaved(updated);
    } catch (err) {
      onError(err.response?.data?.error || 'Could not rename this replay.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-3 pt-3 border-t border-gray-50 space-y-2">
      <label className="block">
        <span className="block text-xs font-medium text-gray-500 mb-1">Name members see on the Replays page (leave blank to use the event name)</span>
        <input value={title} onChange={e => setTitle(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </label>
      <button type="submit" disabled={saving}
        className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50">
        {saving ? 'Saving…' : 'Save name'}
      </button>
    </form>
  );
}

const REPEAT_OPTIONS = [
  { value: 'none',     label: "Doesn't repeat" },
  { value: 'daily',    label: 'Daily' },
  { value: 'weekdays', label: 'Every weekday (Mon–Fri)' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly',  label: 'Monthly' },
];

// Each person's own zone, remembered by the server the last time they saved
// an event (To-Do ec184d07, 2026-10-06) — the form no longer starts on AT
// for everyone. The time typed is NEVER rewritten when the zone changes:
// the zone just says which clock that typed time is on.
function useMyEventZone(setTimezone) {
  useEffect(() => {
    let alive = true;
    adminService.getMyEventZone().then(z => { if (alive && z) setTimezone(z); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function TimezoneSelect({ value, onChange, typedDatetime }) {
  // Live "that's X AT · Y ET" so whoever is typing can see both clocks.
  let both = null;
  if (typedDatetime && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(typedDatetime)) {
    const d = naiveToDateInZone(typedDatetime, value);
    if (d) both = `${formatTimeInZone(d, 'AT')} AT · ${formatTimeInZone(d, 'ET')} ET`;
  }
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">Time zone of the time you typed</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
        <option value="AT">Atlantic (AT)</option>
        <option value="ET">Eastern (ET)</option>
      </select>
      {both && <p className="text-[11px] text-gray-500 mt-1">That's {both}</p>}
    </div>
  );
}

// Same conversion as the server's parseNaiveDatetimeInZone (zonedTime.js).
function naiveToDateInZone(naive, zoneKey) {
  const [datePart, timePart] = naive.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  if ([y, mo, d, h, mi].some(n => Number.isNaN(n))) return null;
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE_IANA[zoneKey] || TIMEZONE_IANA.AT, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date(guess)).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const read = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) % 24, Number(parts.minute));
  return new Date(guess + (guess - read));
}

function LengthInput({ value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">Length (minutes)</label>
      <input type="number" min="5" max="600" step="5" value={value} onChange={e => onChange(e.target.value)} required
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
    </div>
  );
}

function eventLengthMinutes(ev) {
  if (!ev?.event_datetime || !ev?.event_end_datetime) return 60;
  const m = Math.round((new Date(ev.event_end_datetime) - new Date(ev.event_datetime)) / 60000);
  return m > 0 ? m : 60;
}

function CreateEventForm({ onCreated, onCreatedRecurring, onError }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDatetime, setEventDatetime] = useState('');
  const [timezone, setTimezone] = useState('AT');
  useMyEventZone(setTimezone);
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [colorId, setColorId] = useState('');
  const [repeat, setRepeat] = useState('none');
  const [repeatEndDate, setRepeatEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  // Jenn, 2026-09-03 (real live-event failure): a Zoom meeting created
  // directly in Zoom never reaches us automatically — this form used to
  // only be able to mint a brand-new Zoom meeting, which produced a second,
  // wrong link instead of the one already shared with members.
  const [linkSource, setLinkSource] = useState('new'); // 'new' | 'existing'
  const [existingJoinUrl, setExistingJoinUrl] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title: title.trim(), description: description.trim(), eventDatetime, timezone, colorId, durationMinutes: Number(durationMinutes) };
      if (linkSource === 'existing') {
        payload.existingJoinUrl = existingJoinUrl.trim();
      } else if (repeat !== 'none') {
        payload.repeat = repeat; payload.repeatEndDate = repeatEndDate;
      }
      const result = await adminService.createHubEvent(payload);
      if (result.recurring) onCreatedRecurring(result);
      else onCreated(result);
    } catch (err) {
      onError(err.response?.data?.error || 'Could not create this event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 mb-4 space-y-3">
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-xs font-medium w-fit">
        <button type="button" onClick={() => setLinkSource('new')}
          className={`px-3 py-1.5 rounded-md transition-colors ${linkSource === 'new' ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500'}`}>
          Create a new Zoom meeting
        </button>
        <button type="button" onClick={() => setLinkSource('existing')}
          className={`px-3 py-1.5 rounded-md transition-colors ${linkSource === 'existing' ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500'}`}>
          Use a link you already have
        </button>
      </div>
      <p className="text-xs text-gray-400">
        {linkSource === 'new'
          ? 'Creates a real Zoom meeting on the shared account right away — the join link exists the moment the event does.'
          : "For a meeting you already started in Zoom (or any other join link) — we won't touch Zoom at all, just record this link as the event's join link."}
      </p>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input value={title} onChange={e => setTitle(e.target.value)} required
          placeholder="e.g. Hub: Writing Sprint"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
          placeholder="What members see in the calendar invite/Zoom details"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      {linkSource === 'existing' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Join link</label>
          <input value={existingJoinUrl} onChange={e => setExistingJoinUrl(e.target.value)} required type="url"
            placeholder="https://us06web.zoom.us/j/..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Date &amp; time (first occurrence)</label>
        <input type="datetime-local" value={eventDatetime} onChange={e => setEventDatetime(e.target.value)} required
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      </div>
      <TimezoneSelect value={timezone} onChange={setTimezone} typedDatetime={eventDatetime} />
      <LengthInput value={durationMinutes} onChange={setDurationMinutes} />
      <ColorSelect value={colorId} onChange={setColorId} />
      {linkSource === 'new' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Repeat</label>
          <select value={repeat} onChange={e => setRepeat(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
            {REPEAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}
      {linkSource === 'new' && repeat !== 'none' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Repeat until</label>
          <input type="date" value={repeatEndDate} onChange={e => setRepeatEndDate(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
          <p className="text-[11px] text-gray-400 mt-1">Each occurrence becomes its own event on the calendar — editing or canceling one won't touch the others.</p>
        </div>
      )}
      <button type="submit" disabled={saving}
        className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
        {saving ? 'Creating…' : (linkSource === 'new' && repeat !== 'none' ? 'Create series' : 'Create event')}
      </button>
    </form>
  );
}

function EditEventForm({ event, onSaved, onSavedRecurring, onSavedSeries, onError }) {
  const [title, setTitle] = useState(event.title || '');
  const [description, setDescription] = useState(event.description || '');
  // Shown on the clock it was typed in (Trish types ET); older events that
  // don't know yet start on the editor's own zone.
  const [timezone, setTimezone] = useState(event.entered_zone || 'AT');
  const [eventDatetime, setEventDatetime] = useState(toDatetimeLocalValueInZone(event.event_datetime, event.entered_zone || 'AT'));
  const [durationMinutes, setDurationMinutes] = useState(String(eventLengthMinutes(event)));
  const [colorId, setColorId] = useState(event.gcal_color_id || '');
  const [applyToSeries, setApplyToSeries] = useState(false);
  const [repeat, setRepeat] = useState('none');
  const [repeatEndDate, setRepeatEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event.entered_zone) return;
    let alive = true;
    adminService.getMyEventZone().then(z => {
      if (!alive || !z) return;
      setTimezone(z);
      setEventDatetime(toDatetimeLocalValueInZone(event.event_datetime, z));
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A Calendar-only event can be given a Zoom link and made to repeat; a
  // one-off Zoom event can be made to repeat on its own meeting (same link).
  const canMakeRecurring = !event.zoom_meeting_id;
  const canRepeatZoom = !!event.zoom_meeting_id && !event.zoom_occurrence_id;
  const isPartOfSeries = !!event.zoom_occurrence_id || !!event.gcal_recurring_event_id;
  const lockFields = canMakeRecurring && repeat !== 'none';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (canMakeRecurring && repeat !== 'none') {
        const result = await adminService.attachRecurringZoom(event.id, { repeat, repeatEndDate, colorId, timezone, durationMinutes: Number(durationMinutes) });
        onSavedRecurring(result);
      } else {
        const payload = { title: title.trim(), description: description.trim(), eventDatetime, timezone, colorId, durationMinutes: Number(durationMinutes), applyToSeries: isPartOfSeries && applyToSeries };
        if (canRepeatZoom && repeat !== 'none') { payload.repeat = repeat; payload.repeatEndDate = repeatEndDate; }
        const result = await adminService.updateHubEvent(event.id, payload);
        if (result.recurring) onSavedRecurring(result);
        else if (result.series) onSavedSeries(result);
        else onSaved(result);
      }
    } catch (err) {
      onError(err.response?.data?.error || 'Could not update this event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input value={title} onChange={e => setTitle(e.target.value)} required disabled={lockFields}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta disabled:bg-gray-50 disabled:text-gray-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} disabled={lockFields}
          placeholder="What members see in the calendar invite/Zoom details"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta disabled:bg-gray-50 disabled:text-gray-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Date &amp; time</label>
        <input type="datetime-local" value={eventDatetime} onChange={e => setEventDatetime(e.target.value)} required disabled={lockFields}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta disabled:bg-gray-50 disabled:text-gray-400" />
      </div>
      <TimezoneSelect value={timezone} onChange={setTimezone} typedDatetime={eventDatetime} />
      <LengthInput value={durationMinutes} onChange={setDurationMinutes} />
      <ColorSelect value={colorId} onChange={setColorId} />
      {isPartOfSeries && repeat === 'none' && (
        <div className="bg-reset-cream rounded-lg p-3 space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-reset-brown cursor-pointer">
            <input type="radio" checked={!applyToSeries} onChange={() => setApplyToSeries(false)} />
            This event only
          </label>
          <label className="flex items-center gap-2 text-sm text-reset-brown cursor-pointer">
            <input type="radio" checked={applyToSeries} onChange={() => setApplyToSeries(true)} />
            All upcoming events in this series
          </label>
        </div>
      )}
      {canRepeatZoom && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Repeat</label>
          <select value={repeat} onChange={e => setRepeat(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
            {REPEAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}
      {canRepeatZoom && repeat !== 'none' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Repeat until</label>
          <input type="date" value={repeatEndDate} onChange={e => setRepeatEndDate(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
          <p className="text-[11px] text-gray-400 mt-1">Keeps the same Zoom link. Every date goes on Zoom and Google Calendar.</p>
        </div>
      )}
      {canMakeRecurring && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Give it a Zoom link &amp; repeat</label>
          <select value={repeat} onChange={e => setRepeat(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
            {REPEAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}
      {canMakeRecurring && repeat !== 'none' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Repeat until</label>
          <input type="date" value={repeatEndDate} onChange={e => setRepeatEndDate(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
          <p className="text-[11px] text-gray-400 mt-1">
            Creates a real weekly Zoom series from this event's title/time and retires the old Calendar-only copies here.
            The recurring event still exists on Google Calendar itself — end it there too once this is live, so it doesn't keep showing separately.
          </p>
        </div>
      )}
      <button type="submit" disabled={saving}
        className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50">
        {saving ? 'Saving…' : (repeat !== 'none' ? (canRepeatZoom ? 'Make it repeat' : 'Create Zoom series') : 'Save changes')}
      </button>
    </form>
  );
}
